import axios, { AxiosError } from 'axios'

import { refreshAccessToken } from '@/helpers/api/authenticationApi'
import {
    apiRefreshTokenStorage,
    apiTokenStorage,
    authenticatedStorage,
    tenantStorage,
} from '@/helpers/authStorage'
import { logout } from '@/redux/app/appLegacyActions'
import { setApiToken, setRefreshToken } from '@/redux/authentication/authenticationActions'
import { TypedDispatch } from '@/redux/store'

import { version } from '../../package.json'

export class AuthInterceptor {
    store: { dispatch: TypedDispatch }

    constructor(store: { dispatch: TypedDispatch }) {
        this.store = store
    }

    setInterceptors = (): void => {
        const tenant = tenantStorage.get() || process.env.REACT_APP_TENANT || window.location.hostname

        axios.interceptors.request.use(
            (requestConfig) => {
                requestConfig.headers.authorization = `Bearer ${apiTokenStorage.get()}`
                requestConfig.headers.Tenant = tenant
                requestConfig.headers.App = 'employee'
                requestConfig.headers['App-Version'] = version

                return requestConfig
            },
            err => Promise.reject(err),
        )

        const { dispatch } = this.store
        const interceptor = axios.interceptors.response.use(
            response => response,
            (error: AxiosError) => {
                if ((error.response?.status !== 401)) {
                    return Promise.reject(error)
                }

                axios.interceptors.response.eject(interceptor)

                const apiRefreshToken = apiRefreshTokenStorage.get()
                if (! apiRefreshToken || apiRefreshToken === 'undefined') {
                    return Promise.reject(error)
                }

                return refreshAccessToken(apiRefreshToken)
                    .then((refreshResponse) => {
                        dispatch(setApiToken(refreshResponse.data.token))
                        dispatch(setRefreshToken(refreshResponse.data.refresh_token))

                        if (! error.config) {
                            return Promise.reject(error)
                        }

                        error.config.headers['authorization'] = `Bearer ${refreshResponse.data.token}`

                        return axios(error.config)
                    })
                    .catch((refreshError: AxiosError) => {
                        // Without a response (e.g. a network error) the user stays logged in, like before
                        if (! refreshError.response) {
                            throw refreshError
                        }

                        if ((refreshError.response.status !== 200)) {
                            dispatch(logout())
                            authenticatedStorage.set(false)
                        }
                    })
                    // TODO: this re-registers all interceptors (also the request one) and is evaluated before the request
                    .finally(authenticatedStorage.get() ? this.setInterceptors : undefined)
            },
        )
    }
}
